/**
 * Analytics Context Provider and Hook
 *
 * This module provides analytics tracking functionality for:
 * - Listing views and searches
 * - Quote views
 * - Reservations
 * - Gift card purchases
 *
 * The analytics data is pushed to analytics pipelines via the dataLayer.
 * @see https://www.notion.so/Events-Variables-and-Dimensions-153301efe8e440c186e835414fddc4a6
 */
// Third Party
import {
  Account,
  GiftCard,
  Listing,
  PriceDisplay,
  QuoteItemizedType,
  ListingsSearchType,
} from '@gql';
import { isbot } from 'isbot';
import { toInteger } from 'lodash';
import sha256 from 'crypto-js/sha256';

// Utils
import * as React from 'react';
import { formatDollarAmountNumberOnly } from '@utils/money';
import { getSearchSession, setSearchSession } from '@utils/search-session';

import { useSession } from 'next-auth/react';
import moment from 'moment';

// Types
export type AnalyticsContext = {
  setCurrentListing: (listing: AnalyticsListing) => void;
  setListings: (listings: AnalyticsListing[]) => void;
  setSearch: (search: AnalyticsSearch | null) => void;
  emitViewQuoteEvent: (quote: AnalyticsQuote) => void;
  emitViewQuoteComparisonEvent: (
    quote: AnalyticsQuote,
    comparisonQuotes: AnalyticsQuoteComparison[]
  ) => void;
  emitReservationPurchaseEvent: (reservation: AnalyticsReservation) => void;
  emitGiftCardPurchaseEvent: (giftCard: AnalyticsGiftCard) => void;
  emitBeginQuoteCheckoutEvent: (quote: AnalyticsQuote) => void;
  emitGenerateLeadEvent: (lead: {
    source: string;
    account_name: string;
  }) => void;
  emitSignUpBeginEvent: (provider: string) => void;
  emitSignUpEvent: (user: AnalyticsUser) => void;
};

export type AnalyticsListing = Pick<
  Listing,
  'name' | 'slug' | 'id' | 'listingLevel' | 'searchPromotionType'
> & {
  account: Pick<Account, 'companyName' | 'slug' | 'avatarUrl'>;
  priceDisplay?: Pick<PriceDisplay, 'baseAmountCents' | 'nights'>;
  searchPromotionType?: Listing['searchPromotionType'];
};

export type AnalyticsQuote = {
  __typename: 'BookingQuote';
  totalWithoutTaxesCents?: number;
  totalCostCents?: number;
  checkInDate?: string;
  checkOutDate?: string;
  listing: {
    priceDisplay?: Pick<
      PriceDisplay,
      'baseAmountCents' | 'subtotalWithoutTaxesCents'
    >;
  };
  quoteItems: Array<{
    costCents: number;
    type: QuoteItemizedType;
    description: string;
  }>;
};

export type AnalyticsQuoteComparison =
  | {
      checkInDate: string;
      checkOutDate: string;
      externalListingId: string;
      externalSource: string;
      priceCents: number | null;
    }
  | {
      externalSource: string;
    };

export type AnalyticsReservation = {
  __typename: 'Reservation';
  id: string;
  totalCostCents?: number;
  startDate: string;
  endDate: string;
  listing: {
    slug: string;
  };
  guest?: {
    email?: string;
    phone?: string;
    firstName?: string;
    lastName?: string;
    id?: string;
  };
};

export type AnalyticsGiftCard = Pick<
  GiftCard,
  'orderAmountCents' | 'id' | 'shippingFeeCents'
>;

export type AnalyticsSearch = {
  id: string;
  type?: ListingsSearchType;
  destination?: string;
  guestCount?: number;
  checkInDate?: string;
  checkOutDate?: string;
};

// https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#add_payment_info_item
export type ItemParams = {
  item_id: string; // required by GA4 item schema
  item_name: string; // required by GA4 item schema
  index?: number; // built-in item-level dimension
  price?: string; // built-in item-level dimension
  account_name?: string; // custom item-level dimension
  account_id?: string; // custom item-level dimension
  listing_id?: string; // custom item-level dimension
  listing_name?: string; // custom item-level dimension
  subscription_level?: string; // custom item-level dimension
  search_variant?: string; // custom item-level dimension
};

// https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtag
// https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#purchase
// https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#begin_checkout
export type EcomEventParams = {
  value: number | null;
  currency: string;
  check_in_date: string | null;
  check_out_date: string | null;
  quantity: number | null;
  transaction_id?: string;
  tax?: number | null;
  fees?: number | null;
  price_preview?: number | null;
  preview_value?: number | null;
  preview_fees?: number | null;
  airbnb_value?: number | null;
  vrbo_value?: number | null;
};

export type AnalyticsUser = {
  id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
};

// Context
const AnalyticsContext = React.createContext<AnalyticsContext>(undefined!);

// Provider Component
interface Props {
  children: React.ReactNode;
}

export const AnalyticsProvider: React.FC<Props> = ({ children }) => {
  const { data: session } = useSession();
  const [currentListing, setCurrentListingState] =
    React.useState<AnalyticsListing | null>(null);
  const [listings, setListingsState] = React.useState<AnalyticsListing[]>([]);
  const [searchState, setSearchState] = React.useState<AnalyticsSearch | null>(
    null
  );
  const [page, _setPage] = React.useState<number>(1);
  const [pageSize, _setPageSize] = React.useState<number>(30);

  // Main analytics event emitters
  const setCurrentListing = (listing: AnalyticsListing) => {
    // note: the view_item is determined by client tag configuration
    // and not ever pushed to the data layer
    setCurrentListingState(listing);
  };

  const setListings = (listings: AnalyticsListing[]) => {
    setListingsState(listings);
    dl_push({
      event: 'view_item_list',
      items: listings.map(toItemParams),
    });
  };

  const setSearch = (search: AnalyticsSearch | null) => {
    if (!search) return;

    setSearchState(search);

    // GTM tracking
    dl_push({
      event: 'search',
      search_term: search.destination,
    });
  };

  // assumes the current listing has already been pushed to the data layer
  const emitViewQuoteEvent = (quote: AnalyticsQuote) => {
    const params = toEcomEventParams(quote);

    dl_push({
      event: 'view_quote',
      ...params,
    });
  };

  const emitViewQuoteComparisonEvent = (
    quote: AnalyticsQuote,
    comparisonQuotes: AnalyticsQuoteComparison[]
  ) => {
    const params = quoteComparisonsToEconEventParams(quote, comparisonQuotes);

    dl_push({ event: 'view_quote_comparison', ...params });
  };

  // assumes the current listing has already been pushed to the data layer
  const emitBeginQuoteCheckoutEvent = (quote: AnalyticsQuote) => {
    const ecomParams = toEcomEventParams(quote);

    dl_push({
      event: 'begin_checkout',
      ...ecomParams,
    });
  };

  const emitReservationPurchaseEvent = (reservation: AnalyticsReservation) => {
    const ecomParams = toEcomEventParams(reservation);

    dl_push({
      event: 'purchase',
      ...ecomParams,
    });
  };

  const emitGiftCardPurchaseEvent = (giftCard: AnalyticsGiftCard) => {
    // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?sjid=9479731866245626761-NC&client_type=gtag#purchase
    const orderAmount = giftCard.orderAmountCents / 100.0;
    const shippingAmount = giftCard.shippingFeeCents / 100.0;

    dl_push({
      event: 'purchase',
      currency: 'USD',
      value: orderAmount,
      transaction_id: giftCard.id,
      shipping: shippingAmount,
      items: [
        {
          item_name: 'Gift Card',
          item_id: giftCard.id,
          price: orderAmount - shippingAmount,
          currency: 'USD',
          quantity: 1,
        },
      ],
    });
  };

  const emitGenerateLeadEvent = (lead: {
    source: string;
    account_name: string;
  }) => {
    dl_push({
      event: 'generate_lead',
      lead_source: lead.source,
      account_name: lead.account_name,
    });
  };

  const emitSignUpBeginEvent = (provider: string) => {
    dl_push({
      event: 'sign_up_begin',
      provider: provider,
    });
  };

  const emitSignUpEvent = (user: AnalyticsUser) => {
    dl_push({
      event: 'sign_up',
      user_data: toUserData(user),
    });
  };

  // https://www.notion.so/Events-Variables-and-Dimensions-153301efe8e440c186e835414fddc4a6?pvs=4
  // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#select_item_item
  // the schema for a listing is the GA4 canonical item
  // plus any additional custom item-level dimensions.
  // This is so that dataLayer.listings can be passed directly
  // to item event like `view_item_list`, etc as a parameter.
  const toItemParams = (
    listing: AnalyticsListing,
    index?: number
  ): ItemParams => {
    const baseItem: ItemParams = {
      item_id: listing.slug || '',
      item_name: listing.name || '',
      account_name: listing.account.companyName,
      account_id: listing.account.slug,
      listing_id: listing.slug,
      listing_name: listing.name,
      subscription_level: listing.listingLevel,
    };

    if (index !== undefined) {
      baseItem.index = (page - 1) * pageSize + index + 1;
    }

    if (listing.priceDisplay) {
      baseItem.price = formatDollarAmountNumberOnly(
        toInteger(
          listing.priceDisplay.baseAmountCents / listing.priceDisplay.nights
        )
      );
    }

    if (listing.searchPromotionType) {
      baseItem.search_variant = listing.searchPromotionType;
    }

    return baseItem;
  };

  // Private helper functions
  const toUserData = (user: AnalyticsUser) => {
    return {
      id: user.id,
      email: user.email,
      sha256_email_address: user.email ? sha256(user.email).toString() : null,
      address: {
        first_name: user.firstName,
        sha256_first_name: user.firstName
          ? sha256(user.firstName).toString()
          : null,
        last_name: user.lastName,
        sha256_last_name: user.lastName
          ? sha256(user.lastName).toString()
          : null,
      },
    };
  };

  const parseQuoteParams = (quote: AnalyticsQuote): EcomEventParams => {
    const valueCents = quote.totalWithoutTaxesCents || quote.totalCostCents;
    const baseAmountCents = quote.listing?.priceDisplay?.baseAmountCents;
    const previewValue =
      quote.listing?.priceDisplay?.subtotalWithoutTaxesCents ||
      quote.listing?.priceDisplay?.baseAmountCents;
    const feesValue = quote.listing?.priceDisplay?.subtotalWithoutTaxesCents
      ? quote.listing.priceDisplay.subtotalWithoutTaxesCents -
        quote.listing.priceDisplay.baseAmountCents
      : null;

    const categorizedQuoteItems = quote.quoteItems.reduce(
      (acc, item) => {
        if (!acc[item.type]) {
          acc[item.type] = [];
        }
        acc[item.type].push(item);
        return acc;
      },
      {} as Record<QuoteItemizedType, typeof quote.quoteItems>
    );

    const totalFeesCents =
      categorizedQuoteItems['FEE']?.reduce(
        (acc, item) => acc + item.costCents,
        0
      ) || null;

    const totalTaxesCents =
      categorizedQuoteItems['TAX']?.reduce(
        (acc, item) => acc + item.costCents,
        0
      ) || null;

    const numberOfNights =
      quote.checkInDate && quote.checkOutDate
        ? moment(quote.checkOutDate).diff(moment(quote.checkInDate), 'days')
        : null;

    return {
      value: valueCents ? valueCents / 100 : null,
      tax: totalTaxesCents ? totalTaxesCents / 100 : null,
      fees: totalFeesCents ? totalFeesCents / 100 : null,
      price_preview: baseAmountCents ? baseAmountCents / 100 : null,
      preview_value: previewValue ? previewValue / 100 : null,
      preview_fees: feesValue ? feesValue / 100 : null,
      check_in_date: quote.checkInDate || null,
      check_out_date: quote.checkOutDate || null,
      quantity: numberOfNights,
      currency: 'USD',
    };
  };

  const parseReservationParams = (
    reservation: AnalyticsReservation
  ): EcomEventParams => {
    const valueCents = reservation.totalCostCents;
    const numberOfNights = moment(reservation.endDate).diff(
      moment(reservation.startDate),
      'days'
    );

    return {
      value: valueCents ? valueCents / 100 : null,
      check_in_date: reservation.startDate,
      check_out_date: reservation.endDate,
      quantity: numberOfNights,
      transaction_id: reservation.id,
      currency: 'USD',
    };
  };

  const toEcomEventParams = (
    source: AnalyticsQuote | AnalyticsReservation
  ): EcomEventParams => {
    switch (source.__typename) {
      case 'BookingQuote':
        return parseQuoteParams(source);
      case 'Reservation':
        return parseReservationParams(source);
    }
  };

  const quoteComparisonsToEconEventParams = (
    quote: AnalyticsQuote,
    comparisonQuotes: AnalyticsQuoteComparison[]
  ): EcomEventParams => {
    const valueCents = quote.totalWithoutTaxesCents;
    const numberOfNights =
      quote.checkInDate && quote.checkOutDate
        ? moment(quote.checkOutDate).diff(moment(quote.checkInDate), 'days')
        : null;

    let airbnb_value, vrbo_value;
    for (const comparisonQuote of comparisonQuotes) {
      if ('priceCents' in comparisonQuote) {
        if (comparisonQuote.externalSource === 'airbnb') {
          airbnb_value = comparisonQuote.priceCents;
        } else if (comparisonQuote.externalSource === 'vrbo') {
          vrbo_value = comparisonQuote.priceCents;
        }
      }
    }

    return {
      value: valueCents ? valueCents / 100 : null,
      airbnb_value: airbnb_value ? airbnb_value / 100 : null,
      vrbo_value: vrbo_value ? vrbo_value / 100 : null,
      check_in_date: quote.checkInDate || null,
      check_out_date: quote.checkOutDate || null,
      quantity: numberOfNights,
      currency: 'USD',
    };
  };

  const dl_push = (data: object) => {
    // console.log('dl_push', data);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
  };

  React.useEffect(() => {
    // note: isbot does not distinguish between good bots and bad bots
    // we are simply reporting if this is likely not a human user
    // based on the good-faith declaration of the user-agent string
    dl_push({ bot: isbot(navigator.userAgent) });
  }, []);

  // Set current user from session when available
  // see GA4 docs for user_data schema:
  // https://support.google.com/analytics/answer/14171268?hl=en&ref_topic=14175491&sjid=5791962181145330240-NC#fields_to_set_ts
  React.useEffect(() => {
    if (session?.user) {
      dl_push({
        user_data: toUserData(session.user),
      });
    } else {
      dl_push({ user_data: null });
    }
  }, [session]);

  // load previous search state from session when available
  React.useEffect(() => {
    const previousSearch = getSearchSession(undefined);

    if (!searchState && previousSearch) {
      setSearchState(previousSearch as AnalyticsSearch);
    }
  }, []);

  // push current listing to data layer when set
  React.useEffect(() => {
    if (currentListing) {
      const index = listings.findIndex(
        (listing) => listing.id === currentListing.id
      );
      dl_push({
        current_listings: [
          toItemParams(currentListing, index == -1 ? undefined : index),
        ],
      });
    } else {
      dl_push({ current_listings: [] });
    }
  }, [currentListing]);

  // push listings to data layer when set
  React.useEffect(() => {
    if (listings.length) {
      dl_push({
        listings: listings.map((listing, index) =>
          toItemParams(listing, index)
        ),
      });
    } else {
      dl_push({ listings: [] });
    }
  }, [listings]);

  // push search state to data layer when set
  React.useEffect(() => {
    if (searchState !== undefined) {
      dl_push({
        search_destination: searchState?.destination,
        search_type: searchState?.type,
        guest_count: searchState?.guestCount,
        check_in_date: searchState?.checkInDate,
        check_out_date: searchState?.checkOutDate,
      });
      setSearchSession(undefined, searchState);
    }
  }, [searchState]);

  return (
    <AnalyticsContext.Provider
      value={{
        setCurrentListing,
        setListings,
        setSearch,
        emitViewQuoteEvent,
        emitViewQuoteComparisonEvent,
        emitReservationPurchaseEvent,
        emitGiftCardPurchaseEvent,
        emitBeginQuoteCheckoutEvent,
        emitGenerateLeadEvent,
        emitSignUpBeginEvent,
        emitSignUpEvent,
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = () => {
  const context = React.useContext(AnalyticsContext);

  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }

  return context;
};
